import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AddUserModal = ({ isOpen, onClose, onAddUser }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
    currentRole: '',
    phoneNumber: '',
    employeeId: '',
    location: '',
    manager: '',
    team: '',
    accessLevel: '',
    permissions: [],
    mfaEnabled: false,
    sessionTimeout: 30
  });

  const [errors, setErrors] = useState({});

  const departments = [
    'IT Security',
    'Compliance',
    'Finance', 
    'HR',
    'Operations',
    'Legal'
  ];

  const roles = [
    'Security Administrator',
    'Compliance Manager',
    'Department User',
    'HR Manager',
    'Financial Analyst',
    'Security Analyst',
    'Compliance Specialist',
    'Operations Manager',
    'Legal Counsel',
    'Auditor'
  ];

  const accessLevels = [
    'Level 1 - Basic Access',
    'Level 2 - Standard Access',
    'Level 3 - Analyst Access',
    'Level 4 - Management Access',
    'Level 5 - Executive Access'
  ];

  const availablePermissions = [
    { id: 'user-management', label: 'User Management' },
    { id: 'system-admin', label: 'System Admin' },
    { id: 'audit-access', label: 'Audit Access' },
    { id: 'security-monitoring', label: 'Security Monitoring' },
    { id: 'emergency-access', label: 'Emergency Access' },
    { id: 'policy-management', label: 'Policy Management' },
    { id: 'report-access', label: 'Report Access' },
    { id: 'compliance-review', label: 'Compliance Review' },
    { id: 'audit-trail', label: 'Audit Trail' },
    { id: 'basic-access', label: 'Basic Access' },
    { id: 'department-reports', label: 'Department Reports' },
    { id: 'financial-data-view', label: 'Financial Data View' },
    { id: 'hr-access', label: 'HR Access' },
    { id: 'employee-management', label: 'Employee Management' },
    { id: 'payroll-access', label: 'Payroll Access' },
    { id: 'recruitment-tools', label: 'Recruitment Tools' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePermissionChange = (permissionId, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev.permissions, permissionId]
        : prev.permissions.filter(p => p !== permissionId)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.currentRole) newErrors.currentRole = 'Role is required';
    if (!formData.employeeId?.trim()) newErrors.employeeId = 'Employee ID is required';
    if (!formData.accessLevel) newErrors.accessLevel = 'Access Level is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const newUser = {
      id: Date.now(), // Simple ID generation for demo
      ...formData,
      lastLogin: 'Never',
      status: 'pending',
      avatar: 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg',
      hireDate: new Date().toISOString().split('T')[0],
      lastPasswordChange: new Date().toISOString().split('T')[0],
      accountLocked: false,
      loginAttempts: 0,
      passwordExpiry: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days from now
      roleDescription: `${formData.currentRole} with ${formData.accessLevel}`,
      recentActivity: [
        { action: 'Account created', timestamp: new Date().toISOString() }
      ]
    };

    onAddUser(newUser);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      department: '',
      currentRole: '',
      phoneNumber: '',
      employeeId: '',
      location: '',
      manager: '',
      team: '',
      accessLevel: '',
      permissions: [],
      mfaEnabled: false,
      sessionTimeout: 30
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-modal p-4">
      <div className="neumorphic-card w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-text-dark-primary">Add New User</h2>
              <p className="text-text-dark-secondary text-sm mt-1">Create a new user account with roles and permissions</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg text-text-dark-secondary hover:text-text-dark-primary hover:bg-dark-elevated hover:bg-opacity-30 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
                Basic Information
              </h3>
              
              <Input
                label="Full Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter full name"
                error={errors.name}
                required
              />

              <Input
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="user@company.com"
                error={errors.email}
                required
              />

              <Input
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                placeholder="+1 (555) 123-4567"
              />

              <Input
                label="Employee ID"
                value={formData.employeeId}
                onChange={(e) => handleInputChange('employeeId', e.target.value)}
                placeholder="EMP001"
                error={errors.employeeId}
                required
              />
            </div>

            {/* Work Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
                Work Information
              </h3>

              <Select
                label="Department"
                value={formData.department}
                onChange={(value) => handleInputChange('department', value)}
                options={departments.map(dept => ({ value: dept, label: dept }))}
                placeholder="Select department"
                error={errors.department}
                required
              />

              <Select
                label="Role"
                value={formData.currentRole}
                onChange={(value) => handleInputChange('currentRole', value)}
                options={roles.map(role => ({ value: role, label: role }))}
                placeholder="Select role"
                error={errors.currentRole}
                required
              />

              <Input
                label="Manager"
                value={formData.manager}
                onChange={(e) => handleInputChange('manager', e.target.value)}
                placeholder="Manager name"
              />

              <Input
                label="Team"
                value={formData.team}
                onChange={(e) => handleInputChange('team', e.target.value)}
                placeholder="Team name"
              />

              <Input
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="City, State"
              />
            </div>
          </div>

          {/* Access & Security */}
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
              Access & Security
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Access Level"
                value={formData.accessLevel}
                onChange={(value) => handleInputChange('accessLevel', value)}
                options={accessLevels.map(level => ({ value: level, label: level }))}
                placeholder="Select access level"
                error={errors.accessLevel}
                required
              />

              <div>
                <label className="block text-sm font-medium text-text-dark-primary mb-2">
                  Session Timeout (minutes)
                </label>
                <select
                  value={formData.sessionTimeout}
                  onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-dark-secondary border border-dark-border rounded-lg text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-neon-green focus:border-transparent"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                checked={formData.mfaEnabled}
                onChange={(checked) => handleInputChange('mfaEnabled', checked)}
              />
              <label className="text-sm text-text-dark-primary">
                Enable Multi-Factor Authentication (MFA)
              </label>
            </div>
          </div>

          {/* Permissions */}
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-medium text-text-dark-primary border-b border-dark-border pb-2">
              Permissions
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {availablePermissions.map(permission => (
                <div key={permission.id} className="flex items-center space-x-2">
                  <Checkbox
                    checked={formData.permissions.includes(permission.id)}
                    onChange={(checked) => handlePermissionChange(permission.id, checked)}
                  />
                  <label className="text-sm text-text-dark-primary">
                    {permission.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-dark-border">
            <button
              onClick={handleClose}
              className="btn-secondary-dark"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="btn-primary-dark inline-flex items-center space-x-2"
            >
              <Icon name="Plus" size={16} />
              <span>Add User</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;